<template>

    <div class="relative p-2">
        <div v-if="loading" class="absolute inset-0 bg-gray-800 bg-opacity-70 z-50 flex flex-col">
            <div class="w-full flex justify-end p-3">
                <button class="btn-xs btn-danger" @click="setLoading(false)">Unblock</button>
            </div>
            <div class="pt-10 flex items-center justify-center pointer-events-auto">
                <span class="text-gray-200 text-lg">{{loading_message}}</span>
            </div>
        </div>
        <h3>Bulk Order Management</h3>
        <div class="text-xs flex flex-col gap-2 ">

            <div class="p-1 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-800">
                <button class="btn-xs btn-success text-sm" @click="handleRefreshOrders">Refresh</button>
                <div class="py-1">Get all open and hold orders which were created via the API.</div>
            </div>
            <div class="p-1 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-800">
                <button class="btn-xs btn-primary text-sm" @click="handleOpenOrders">Open All Orders</button>
                <div class="py-1">Command to the server to open all held orders which are on this sheet. You can
                    delete rows to exclude any orders from this command.</div>
            </div>
            <div class="p-1 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-800">
                <button class="btn-xs btn-primary text-sm" @click="handleHoldOrders">Hold All Orders</button>
                <div class="py-1">Command to the server to hold all open orders which are on this sheet. You can
                    delete rows to exclude any orders from this command.</div>
            </div>
            <div class="p-1 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-800">
                <button class="btn-xs btn-warning text-sm" @click="handleSubmitOrders">
                    Submit Orders
                </button>
                <div class="py-1">Sends all the orders in the sheet to the server. Orders are sent in a HOLD status by default, unless specified as OPEN.</div>
            </div>
            <div class="p-1 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-800">
                <button class="btn-xs btn-danger text-sm" @click="handleCancelOrders">
                    Cancel All Orders
                </button>
                <div class="py-1">Command the server to cancel all orders which are on this sheet. You can
                    delete rows to exclude any orders from this command.</div>
            </div>
        </div>
        <div class="p-2">Orders staged: {{ orders.length }}</div>
    </div>

</template>

<script setup>
import { onMounted, ref } from 'vue';
import axios from "axios";
import dayjs from "dayjs";

const props = defineProps({
    company: {
        type: Object,
        default: () => ({})
    },
    environment: {
        type: String,
        default: ""
    },
    environmentURL: {
        type: String,
        default: ""
    },
    apiKey: {
        type: String,
        default: ""
    }
});
const company = ref(props.company);
const environment = ref(props.environment);
const environmentURL = ref(props.environmentURL);
const apiKey = ref(props.apiKey);
const loading = ref(false);
const loading_message = ref("");

const sheetHeaders = [
    { id: 1, text: "Asset Code", key: "asset_code" },
    { id: 2, text: "Type", key: "type" },
    { id: 3, text: "Quantity", key: "quantity" },
    { id: 4, text: "Price", key: "price" },
    { id: 7, text: "Status", key: "status" },
    { id: 6, text: "Is Persistent", key: "is_persistent" },
    { id: 5, text: "Replace Match", key: "replace_match" },
    { id: 8, text: "ID", key: "order_id" },
    { id: 9, text: "Updated", key: "updated_at" },
];

const orders = ref([]);

function setLoading(value, message) {
    console.log("setLoading", value, message);
    loading.value = value;
    loading_message.value = message;
}

async function fetchOrdersFromSheet() {
    setLoading(true, "Fetching orders from sheet...");
    console.log('fetching orders from sheet')
    await Excel.run(async (context) => {
        const sheet = await getOrCreateTODOrdersSheet(context);
        const headerCount = sheetHeaders.length;
        // Get used range, but skip header row
        const usedRange = sheet.getUsedRange();
        usedRange.load(["values", "rowCount", "columnCount"]);
        await context.sync();
        // Map each data row to an object with keys from sheetHeaders
        const headerKeys = sheetHeaders.map(h => h.key);
        const rows = usedRange.values;
        if (!rows || rows.length < 2) {
            orders.value = [];
            return;
        }
        const dataRows = rows.slice(1); // skip header
        const filtered = dataRows.map(row => {
            const rowData = {};
            for (let i = 0; i < Math.min(headerCount, row.length); i++) {
                let value = row[i];
                // Convert "Quantity" and "Price" to numbers
                if (headerKeys[i] === "quantity" || headerKeys[i] === "price") {
                    value = parseFloat(value);
                }
                // Parse "Updated" as date
                if (headerKeys[i] === "updated_at") {
                    value = value ? dayjs(value).toDate() : null;
                }
                // check if there is a value in replace_match cell, if not set it as true
                if (headerKeys[i] === "replace_match" && !value) {
                    value = true;
                } else {
                    // else set it to whatever the value of the cell is
                    value = value;
                }
                // check if there is a value in is_persistent cell, if not set it as false
                if (headerKeys[i] === "is_persistent" && !value) {
                    value = false;
                }
                rowData[headerKeys[i]] = value;
            }
            return rowData;
        });

        orders.value = filtered.filter(order => order.type);
        console.log("Fetched orders from sheet:", orders.value);
    });
}


onMounted(async () => {
    try {
        await Excel.run(async (context) => {
            sheetSetup(context);
            handleRefreshOrders();
        });
    } catch (error) {
        console.error("Error accessing or creating the TOD_ORDERS sheet:", error);
    }
});

async function handleRefreshOrders() {
    await getOrdersFromServer().then(
        () => {
            if (orders.value.length === 0) {
                // if no orders from server, try to fetch from sheet
                fetchOrdersFromSheet().then(
                    () => {
                        // if still no orders, insert empty state
                        if (orders.value.length === 0) {
                            orders.value = [];
                            insertOrdersIntoSheet();
                        }
                    }
                );
            } else {
                // if we got orders from server, insert them into sheet
                insertOrdersIntoSheet();
            }
        }
    );
    setLoading(false);
}

async function handleHoldOrders() {
    try {
        const success = await postBulkStatusUpdateRequest("hold", "Holding orders...");
        if (success) {
            bulkUpdateStatusOfLocalOrders("HOLD");
            await getOrdersFromServer().then(() => insertOrdersIntoSheet());
        }
    } catch (error) {
        console.error("Error in handleHoldOrders:", error);
    } finally {
        setLoading(false);
    }
}

async function handleOpenOrders() {
    try {
        const success = await postBulkStatusUpdateRequest("open", "Opening orders...");
        if (success) {
            bulkUpdateStatusOfLocalOrders("OPEN");
            await getOrdersFromServer().then(() => insertOrdersIntoSheet());
        }
    } catch (error) {
        console.error("Error in handleOpenOrders:", error);
    } finally {
        setLoading(false);
    }
}

async function handleCancelOrders() {
    try {
        const success = await postBulkStatusUpdateRequest("cancel", "Cancelling orders...");
        if (success) {
            bulkUpdateStatusOfLocalOrders("CANCELLED");
            await getOrdersFromServer().then(() => insertOrdersIntoSheet());
        }
    } catch (error) {
        console.error("Error in handleCancelOrders:", error);
    } finally {
        setLoading(false);
    }
}

async function handleSubmitOrders() {
    await postBulkOrderCreateRequest().then(success => {
        if (success) {
            bulkUpdateStatusOfLocalOrders("SUBMITTED");
            console.log("Bulk submit request successful");
            getOrdersFromServer().then(() => insertOrdersIntoSheet());
        }
    }).catch(error => {
        console.error("Error in handleSubmitOrders:", error);
    }).finally(() => {
        setLoading(false);
    });
}

async function getOrdersFromServer() {
    setLoading(true, "Getting your API orders from server...");
    console.log("calling getOrdersFromServer()");
    try {
        const response = await axios.get(`${environmentURL.value}/orders`, {
            headers: {
                'Authorization': `Bearer ${apiKey.value}`
            },
            params: {
                tags: 'api',
            }
        });
        console.log("Fetched orders from server:", response.data);
        orders.value = response.data.orders;
    } catch (error) {
        console.error("Error fetching orders from server:", error);
    }
}

function orderIdArray()
{
    return orders.value.map(order => order.order_id);
}

async function postBulkOrderCreateRequest() {
    await fetchOrdersFromSheet();
    setLoading(true, "Submitting orders...");
    const payload = {
        orders: orders.value
    };
    try {
        const response = await axios.post(`${environmentURL.value}/orders`, payload, {
            headers: {
                'Authorization': `Bearer ${apiKey.value}`,
                'Content-Type': 'application/json'
            }
        });
        console.log("Bulk order create response:", response.data);
        return true;
    } catch {
        console.error("Error posting bulk order create request");
        return false;
    }
}


async function postBulkStatusUpdateRequest(status, loadingMessage) {
    await fetchOrdersFromSheet();
    setLoading(true, loadingMessage);
    const payload = {
        order_ulids: orderIdArray()
    };
    try {
        const response = await axios.post(`${environmentURL.value}/orders/${status}`, payload, {
            headers: {
                'Authorization': `Bearer ${apiKey.value}`,
                'Content-Type': 'application/json'
            }
        });
        console.log(`Bulk ${status} response:`, response.data);
        return true;
    } catch (error) {
        console.error(`Error posting bulk ${status} request:`, error);
        return false;
    }
}

async function bulkUpdateStatusOfLocalOrders(status) {
    orders.value = orders.value.map(order => {
        return { ...order, status: status };
    });
    insertOrdersIntoSheet();
}
async function bulkUpdateReplaceMatchOfLocalOrders(value) {
    orders.value = orders.value.map(order => {
        return { ...order, replace_match: value };
    });
    insertOrdersIntoSheet();
}

async function insertOrdersIntoSheet() {
    await Excel.run(async (context) => {
        const sheet = await getOrCreateTODOrdersSheet(context);
        const currentHeaders = sheetHeaders.map(h => h.text);
        // Clear existing data except header
        const usedRange = sheet.getUsedRange();
        usedRange.load(["rowCount", "columnCount"]);
        await context.sync();
        if (usedRange.rowCount > 1) {
            const dataRange = sheet.getRangeByIndexes(1, 0, usedRange.rowCount - 1, usedRange.columnCount);
            dataRange.clear();
            await context.sync();
        }
        // Insert new orders
        if (orders.value.length > 0) {
            const values = orders.value.map(order => {
                return sheetHeaders.map(h => {
                    if (h.key === "updated_at" && order[h.key]) {
                        return dayjs(order[h.key]).format('YYYY-MM-DD HH:mm:ss');
                    }
                    return order[h.key] !== undefined ? order[h.key] : '';
                });
            });
            const insertRange = sheet.getRangeByIndexes(1, 0, values.length, currentHeaders.length);
            insertRange.values = values;
            await context.sync();
        }
    });
}

async function getOrCreateTODOrdersSheet(context) {
    let sheet;
    try {
        sheet = context.workbook.worksheets.getItem("TOD_ORDERS");
        // Try to load to check existence
        sheet.load("name");
        await context.sync();
    } catch (e) {
        // If not found, create it
        sheet = context.workbook.worksheets.add("TOD_ORDERS");
        await context.sync();
    }
    // prevent any ability to manually edit the sheet - it should basically be read-only
    await context.sync();
    return sheet;
}

async function sheetSetup(context) {
    const sheet = await getOrCreateTODOrdersSheet(context);
    sheet.activate();
    const currentHeaders = sheetHeaders.map(h => h.text);
    sheet.getRangeByIndexes(0, 0, 1, currentHeaders.length).values = [currentHeaders];
    await context.sync();
    return sheet;
}

</script>